import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchAdminAccess, fetchRoles, replaceAdminRoles, setPermissionOverride } from "../../api/access";
import PermissionScreenLinks from "../../component/access/PermissionScreenLinks";
import ReasonModal from "../../component/access/ReasonModal";
import { useAccess } from "../../context/AccessContext";
import "../../css/AccessManagement.css";
import {
  OVERRIDE_STATES,
  currentOverrideState,
  describeSource,
  formatDateTime,
  getAccessErrorMessage,
  overrideLabel,
} from "../../utils/accessErrors";
import Layout from "../Layout";
import { adminLabel, localInputToIso, useAdminList, useCatalog } from "./accessData";

/**
 * 권한 관리 — 사람별 보기 (spec R-MGMT-05/06, R-FE-06).
 * 어드민 한 명의 역할 목록과 도메인별 전체 권한의 실효 상태·출처를 보여주고, 역할 배정을 교체한다.
 * 권한마다 3상태 토글(역할 따름/허용/차단)로 이 어드민의 개인 예외를 바꾼다(기능별 보기와 같은 API).
 */
function AccessAdminView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, adminUserId: myAdminUserId, refreshAccess } = useAccess();
  const canManage = hasPermission("access.permission.manage");
  const { admins, error: adminsError } = useAdminList();
  // 사람별 응답에는 screens/hidden 이 없어 카탈로그와 키로 합친다. 카탈로그를 못 받으면 숨김 없이·링크 없이 보여준다.
  const { hiddenKeys, permissionByKey } = useCatalog();

  const selectedId = Number(searchParams.get("adminUserId")) || null;
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [editingRoles, setEditingRoles] = useState(null); // string[] | null
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(null); // { perm, next }
  const [expiresAt, setExpiresAt] = useState("");

  const load = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      setDetail(await fetchAdminAccess(selectedId));
    } catch (err) {
      setDetail(null);
      setError(getAccessErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchRoles()
      .then(setRoles)
      .catch(() => setRoles([]));
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    (detail?.permissions ?? []).forEach((p) => {
      if (hiddenKeys.has(p.key)) return; // 숨김 키는 보이지 않는다(모두 숨김인 도메인은 그룹째 빠진다)
      if (!map.has(p.domain)) map.set(p.domain, []);
      map.get(p.domain).push(p);
    });
    return [...map.entries()];
  }, [detail, hiddenKeys]);

  const currentRoleKeys = (detail?.roles ?? []).map((r) => r.roleKey);

  const toggleRole = (roleKey) =>
    setEditingRoles((prev) => (prev.includes(roleKey) ? prev.filter((k) => k !== roleKey) : [...prev, roleKey]));

  const sameRoles =
    editingRoles &&
    editingRoles.length === currentRoleKeys.length &&
    editingRoles.every((k) => currentRoleKeys.includes(k));

  const confirmRoles = async (reason) => {
    await replaceAdminRoles(selectedId, { roleKeys: editingRoles, reason });
    setConfirmOpen(false);
    setEditingRoles(null);
    await load();
    if (selectedId === myAdminUserId) await refreshAccess();
  };

  const openChange = (perm, next) => {
    if (!canManage || currentOverrideState(perm) === next) return;
    setExpiresAt("");
    setPending({ perm, next });
  };

  const expiresIso = localInputToIso(expiresAt);
  const expiresInvalid = Boolean(expiresAt) && (!expiresIso || new Date(expiresIso) <= new Date());

  const confirmChange = async (reason) => {
    const { perm, next } = pending;
    await setPermissionOverride(perm.key, selectedId, {
      effect: next,
      expiresAt: next === "INHERIT" ? undefined : expiresIso,
      reason,
    });
    setPending(null);
    await load();
    if (selectedId === myAdminUserId) await refreshAccess(); // 내 권한이 바뀌었으면 메뉴·버튼 갱신
  };

  return (
    <Layout>
      <div className="access-page">
        <div>
          <h4 className="access-page-title">권한 관리 · 사람별 보기</h4>
          <p className="access-page-desc">어드민 한 명이 가진 역할과, 모든 권한의 실효 결과·출처를 보여줍니다.</p>
        </div>

        <div className="access-section access-toolbar">
          <select
            className="form-select form-select-sm"
            aria-label="어드민 선택"
            value={selectedId ?? ""}
            onChange={(e) => {
              setEditingRoles(null);
              setSearchParams(e.target.value ? { adminUserId: e.target.value } : {});
            }}
          >
            <option value="">어드민 선택</option>
            {admins.map((a) => (
              <option key={a.adminUserId} value={a.adminUserId}>
                {adminLabel(a)}
              </option>
            ))}
          </select>
        </div>

        {(adminsError || error) && <div className="access-error">{adminsError || error}</div>}

        {!selectedId ? (
          <div className="access-section access-empty">어드민을 선택하세요</div>
        ) : loading && !detail ? (
          <div className="access-section access-empty">불러오는 중...</div>
        ) : detail ? (
          <>
            <div className="access-section">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{detail.name || "(이름 없음)"}</strong>{" "}
                  <span className="access-muted">
                    {detail.email} · #{detail.adminUserId}
                    {detail.adminUserId === myAdminUserId && " · 나"}
                  </span>
                </div>
                {canManage && !editingRoles && (
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setEditingRoles(currentRoleKeys)}>
                    역할 변경
                  </button>
                )}
              </div>

              <div className="access-domain-title">역할</div>
              {!editingRoles ? (
                currentRoleKeys.length === 0 ? (
                  <span className="access-muted">배정된 역할 없음</span>
                ) : (
                  detail.roles.map((r) => (
                    <span key={r.roleKey} className="access-chip" title={r.roleKey}>
                      {r.name} <span className="access-muted">({r.roleKey})</span>
                    </span>
                  ))
                )
              ) : (
                <>
                  <div className="access-check-grid">
                    {roles.map((r) => (
                      <label key={r.roleKey}>
                        <input
                          type="checkbox"
                          checked={editingRoles.includes(r.roleKey)}
                          onChange={() => toggleRole(r.roleKey)}
                        />
                        <span>
                          {r.name} <span className="access-muted">({r.roleKey})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <button type="button" className="btn btn-light btn-sm" onClick={() => setEditingRoles(null)}>
                      취소
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={sameRoles}
                      onClick={() => setConfirmOpen(true)}
                    >
                      저장
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="access-section access-table-wrap">
              {groups.map(([domain, perms]) => (
                <div key={domain}>
                  <div className="access-domain-title">{domain}</div>
                  <table className="table table-sm access-table">
                    <thead>
                      <tr>
                        <th style={{ width: "34%" }}>권한</th>
                        <th>실효 결과</th>
                        <th>출처</th>
                        <th>개인 예외</th>
                        <th>설정</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perms.map((p) => (
                        <tr key={p.key}>
                          <td>
                            <button
                              type="button"
                              className="access-link"
                              onClick={() => navigate(`/access/permissions?key=${encodeURIComponent(p.key)}`)}
                            >
                              {p.description}
                            </button>
                            <div className="access-muted">{p.key}</div>
                            <PermissionScreenLinks screens={permissionByKey.get(p.key)?.screens} />
                          </td>
                          <td>
                            <span className={`access-badge ${p.effective ? "access-badge-allow" : "access-badge-deny"}`}>
                              {p.effective ? "허용" : "차단"}
                            </span>
                          </td>
                          <td>{describeSource(p.source, p.roleKeys)}</td>
                          <td>
                            {p.override ? (
                              <>
                                <span
                                  className={`access-badge ${
                                    p.override.expired
                                      ? "access-badge-expired"
                                      : p.override.effect === "ALLOW"
                                        ? "access-badge-allow"
                                        : "access-badge-deny"
                                  }`}
                                >
                                  {overrideLabel(p.override.effect)}
                                  {p.override.expired && " · 만료됨"}
                                </span>
                                {p.override.expiresAt && (
                                  <div className="access-muted">만료 {formatDateTime(p.override.expiresAt)}</div>
                                )}
                              </>
                            ) : (
                              <span className="access-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div className="access-toggle" role="group" aria-label={`${p.key} 개인 예외 설정`}>
                              {OVERRIDE_STATES.map((s) => (
                                <button
                                  type="button"
                                  key={s.value}
                                  className={currentOverrideState(p) === s.value ? `selected-${s.value}` : ""}
                                  aria-pressed={currentOverrideState(p) === s.value}
                                  disabled={!canManage}
                                  onClick={() => openChange(p, s.value)}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      <ReasonModal
        isOpen={confirmOpen}
        title="역할 배정 변경"
        summary={
          editingRoles && (
            <>
              <div>
                <strong>{detail?.name || `#${selectedId}`}</strong> ({detail?.email})
                {selectedId === myAdminUserId && " — 내 계정"}
              </div>
              <div>현재: {currentRoleKeys.join(", ") || "없음"}</div>
              <div>
                변경 후: <strong>{editingRoles.join(", ") || "없음"}</strong>
              </div>
            </>
          )
        }
        onConfirm={confirmRoles}
        onClose={() => setConfirmOpen(false)}
      />

      <ReasonModal
        isOpen={Boolean(pending)}
        title="개인 권한 변경"
        danger={pending?.next === "DENY"}
        extraInvalid={pending?.next !== "INHERIT" && expiresInvalid}
        summary={
          pending && (
            <>
              <div>
                <strong>{detail?.name || `#${selectedId}`}</strong> ({detail?.email})
                {selectedId === myAdminUserId && " — 내 계정"}
              </div>
              <div>
                {pending.perm.description} <span className="access-muted">({pending.perm.key})</span>
              </div>
              <div>
                {overrideLabel(currentOverrideState(pending.perm))} → <strong>{overrideLabel(pending.next)}</strong>
              </div>
              {pending.next === "INHERIT" && (
                <div className="access-muted">개인 예외를 지우고 역할에 따른 결과로 돌아갑니다.</div>
              )}
            </>
          )
        }
        onConfirm={confirmChange}
        onClose={() => setPending(null)}
      >
        {pending && pending.next !== "INHERIT" && (
          <>
            <label className="access-label" htmlFor="access-expires-at">
              만료일 (선택 — 비우면 만료 없음)
            </label>
            <input
              id="access-expires-at"
              type="datetime-local"
              className="form-control form-control-sm"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            {expiresInvalid && <div className="access-error">만료일은 지금 이후여야 합니다.</div>}
          </>
        )}
      </ReasonModal>
    </Layout>
  );
}

export default AccessAdminView;
